import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ListChecks } from "lucide-react";

import {
  bookingReadinessApi,
  bookingSettingsQueryKeys,
} from "../../shared/api/bookingSettings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * #19939 — báo trước cho phòng khám những gì hệ thống sẽ từ chối.
 *
 * <ul>
 *   <li>TC08: đang bật đặt lịch online mà không có ngày nào trong {@code horizonDays} ngày tới còn
 *       khung giờ → cảnh báo đỏ, kèm lý do theo số ngày. Số liệu đến từ CHÍNH availability phía
 *       server, không tính lại ở đây.
 *   <li>{@code showRules}: danh sách luật đang áp, đọc từ cấu hình thật.
 * </ul>
 */
export function BookingReadinessCard({ showRules = false }: { showRules?: boolean }) {
  const q = useQuery({
    queryKey: bookingSettingsQueryKeys.readiness(),
    queryFn: () => bookingReadinessApi.get(),
    staleTime: 0,
  });
  const r = q.data;
  if (!r) return null;

  const reasons = Object.entries(r.closedReasons ?? {});

  return (
    <div className="mb-4 grid max-w-2xl gap-4">
      {r.noOpenHours && (
        <div
          role="alert"
          data-testid="readiness-no-open-hours"
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <p className="font-medium">
              Bệnh nhân chưa đặt lịch online được: {r.horizonDays} ngày tới không còn giờ trống
              nào.
            </p>
            <p className="mt-0.5">
              Đặt lịch online đang bật, nhưng bệnh nhân mở Mini App / website sẽ không thấy giờ
              nào để chọn.
              {!r.sessionsConfigured &&
                " Chi nhánh chưa khai Buổi khám hay Lịch trực nào — khai ở Cài đặt · Buổi khám và Lịch làm việc (bác sĩ)."}
            </p>
            {reasons.length > 0 && <p className="mt-1">Lý do:</p>}
            {reasons.length > 0 && (
              <ul className="list-disc pl-5">
                {reasons.map(([code, days]) => (
                  <li key={code}>
                    {(r.closedMessages?.[code] ?? code).replace(/\.\s*$/, "")} ({days} ngày)
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      {!r.noOpenHours && r.onlineBookingEnabled && showRules && (
        <p className="text-sm text-muted-foreground" data-testid="readiness-open-days">
          Bệnh nhân đang đặt lịch online được
          {r.firstOpenDate
            ? ` — ngày gần nhất còn giờ trống: ${formatDate(r.firstOpenDate)}`
            : ""}
          .
        </p>
      )}

      {showRules && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ListChecks className="size-4" />
              Điều kiện đặt lịch đang áp dụng
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Tóm tắt các cài đặt đang có hiệu lực. Bệnh nhân chỉ thấy những giờ thoả tất cả điều
              kiện dưới đây.
            </p>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1 pl-5 text-sm" data-testid="readiness-rules">
              {r.rules.map((rule) => (
                <li key={rule.code}>{rule.text}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
